import PubSub from 'pubsub-js';
import { isEqual } from 'lodash';
import { AXIS, CURSOR_MODE, OBJECT_SELECTION_MODE, SELECTED_OBJECT_TYPE } from '../../../../config/constant/unityConstants';
import { getProp } from '../../../../helpers/idb';
import { dispatcher } from '../../../../helpers/projectHelper';
import { getPropLightList, getPropMetadata, isExclusiveProp } from '../../../../helpers/unityHelper';
import { UnityEvent, waitForEventOnce } from '../../../../helpers/unityPubSub';
import { selectIsStyleOnlyMode, selectSelectedAxis, selectSidebarSection } from '../../../../redux/slicers/admin/curatorSlicer';
import {
  selectCopiedObjectProperties,
  selectObjectPositionAlongAxis,
  selectParsedObject,
  selectSelectedObjectList,
  setObjectPositionAlongAxisValue,
  setSelectedObjectList,
} from '../../../../redux/slicers/admin/curatorUnityObjectSlicer';
import { getAppState } from '../../../../redux/store';
import { wait } from '../../../../helpers/jsHelper';
import { ACTION } from './undoRedoConstants';
import { LogCurator } from '../../../../helpers/logEvents';
import { selectReplaceGroupPropActive } from '../../../../redux/slicers/admin/shortcutsModuleSlicer';
import { convertActiveUnitToMeters } from '../../../../helpers/curatorActions';
import { isMattressBuilderPage } from '../ProductBuilderComponents/commonHelper';
import { CURATOR_MENU_SECTION } from '../../../../redux/slicers/admin/curatorMenuPreferencesSlicer';

export const mapObjectToSelect = (obj) => {
  return {
    objectName: obj.objectName,
    ObjectSelectionMode: obj.objectSelectionMode,
    clickedObjectName: obj.clickedObjectName,
  };
};

const DELETE_MODE = {
  DESTROY: 'Destroy',
  INACTIVE: 'Inactive',
}

const CREATE_MODE = {
  CREATE: 'Create',
  ENABLE: 'Enable',
}

export class UndoRedoUnityObject {
  constructor({ undoRedoInstance, unityContext, unityShortcuts }) {
    this.undoRedo = undoRedoInstance;
    this.unityContext = unityContext;
    this.unityShortcuts = unityShortcuts;
    this.ignoreSelectObjectCount = 0;
    this.replacePropInProgress = false;

    this.prevObjectSelectionList = [];

    this.init();

    this.subscribeTokens = [];
    this.subscribe();
  }

  subscribe = () => {
    const token1 = PubSub.subscribe(UnityEvent.OnPropsDuplicate, (_, objectsInfoList) => {
      this.duplicateObject({ objectList: objectsInfoList });
    });

    this.subscribeTokens.push(token1);
  };

  unsubscribe = () => {
    // TODO: refactor it to some parent class
    this.subscribeTokens.forEach((token) => PubSub.unsubscribe(token));
    this.subscribeTokens = [];
  };

  savePrevObjectListForDuplicateUndoSelection = () => {
    // this should happen before data is updated in redux store
    this.prevObjectSelectionList = selectSelectedObjectList(getAppState());
  }

  _isReplacePropInProgress = () => {
    return this.replacePropInProgress;
  };

  startReplaceProp = () => {
    this.replacePropInProgress = true;
  }

  endReplaceProp = () => {
    this.replacePropInProgress = false;
  }

  _getTransformData = (unityObject, key) => {
    return {
      objectName: unityObject.objectName,
      objectTransformInfo: {
        xPosition: unityObject[key].xPosition,
        yPosition: unityObject[key].yPosition,
        zPosition: unityObject[key].zPosition,
        xRotation: unityObject[key].xRotation,
        yRotation: unityObject[key].yRotation,
        zRotation: unityObject[key].zRotation,
        wRotation: unityObject[key].wRotation,
        xScale: unityObject[key].xScale,
        yScale: unityObject[key].yScale,
        zScale: unityObject[key].zScale,
        axis: unityObject[key].axis,
        space: unityObject[key].space,
        hasCutout: unityObject[key].hasCutout,
        cutoutObjectInfo: unityObject[key].cutoutObjectInfo,
        isInGroup: unityObject[key].isInGroup,
        parentObject: unityObject[key].parentObject,
        isResetOperationPerfomedOnGroup: unityObject[key].isResetOperationPerfomedOnGroup,
      },
    };
  };

  _getEnableObjectData = async (unityObject, creationType, config = {}) => {
    const { includeTransform } = config;
    const { type, objectName, newTransformData } = unityObject;
    const id = parseInt(unityObject.id);

    const transformProperties = {
      xPosition: newTransformData.xPosition,
      yPosition: newTransformData.yPosition,
      zPosition: newTransformData.zPosition,
      xRotation: newTransformData.xRotation,
      yRotation: newTransformData.yRotation,
      zRotation: newTransformData.zRotation,
      wRotation: newTransformData.wRotation,
      xScale: newTransformData.xScale,
      yScale: newTransformData.yScale,
      zScale: newTransformData.zScale,
      axis: newTransformData.axis,
      space: newTransformData.space,
      hasCutout: newTransformData.hasCutout,
      cutoutObjectInfo: newTransformData.cutoutObjectInfo,
      isInGroup: newTransformData.isInGroup,
      parentObject: newTransformData.parentObject,
      isResetOperationPerfomedOnGroup: newTransformData.isResetOperationPerfomedOnGroup,
    };

    if (type !== 'Prop') {
      return {
        propModel: {
          objectName,
          ...(includeTransform ? transformProperties : {}),
        },
        creationType,
      };
    }

    const prop = await getProp(id);
    const lightList = await getPropLightList(prop);

    const data = {
      id,
      objectName,

      ...transformProperties,

      count: 1,
      type: 'CommandProp',
      propName: prop.name,
      locationUrl: prop.file,
      roomLightDatas: lightList,
      ...getPropMetadata(prop),
      isExclusiveProp: isExclusiveProp(prop),
    };

    let wallName = []
    if (type === 'Prop') {
      wallName = unityObject?.wallName || [];
    }

    return {
      propModel: data,
      creationType,
      ...(wallName?.length > 0 && { wallName: wallName })
    };
  };

  _mapObjectToSelect = mapObjectToSelect

  _enableObjectList = async (objectList, creationType = 'Create') => {
    const enableObjectPromises = objectList.map(async (object) => {
      this.ignoreSelectObjectCount += 1;
      const objectData = await this._getEnableObjectData(object, creationType);
      this.unityContext.mainController.EnableGameObject(objectData);
    });

    await Promise.all(enableObjectPromises);
    this.unityContext.metadataModule.GetRoomMetadataListReceiver();
  };

  _disableObjectList = async (objectList, deletionType = 'Destroy') => {
    objectList.forEach(async (object) => {
      this.ignoreSelectObjectCount += 1;
      await this.unityContext.mainController.DisableGameObject({
        objectName: object.objectName,
        deletionType,
      });
    });
    this.unityContext.metadataModule.GetRoomMetadataListReceiver();
  };

  _updateObjectPositionAlongAxis = ({ objectPositionAlongAxis, selectedAxis, transformData }) => {
    const tt = objectPositionAlongAxis.transformType;
    const suffix =
      tt === 'Move' ? 'Position' : tt === 'Rotate' ? 'Rotation' : tt === 'Scale' ? 'Scale' : null;

    const prefix = `${selectedAxis}`.toLowerCase();
    const key = `${prefix}${suffix}`;

    if (objectPositionAlongAxis.transformType && selectedAxis !== AXIS.ALL) {
      const value = transformData[key];
      dispatcher(setObjectPositionAlongAxisValue(value));
    }
  };

  init = () => {
    this.trasnform = this.undoRedo.createAction(({ objectList }) => {
      if (!objectList || !objectList.length) return;
      const objectPositionAlongAxis = selectObjectPositionAlongAxis(getAppState());
      const selectedAxis = selectSelectedAxis(getAppState());

      return {
        __ACTION__: 'UNITY_OBJECT__TRANSFORM',
        undo: () => {
          const _objectList = objectList.map((unityObject) => {
            const transformData = this._getTransformData(unityObject, 'oldTransformData');
            // this.unityContext.mainController.SetTransformation(transformData);

            this._updateObjectPositionAlongAxis({
              objectPositionAlongAxis,
              selectedAxis,
              transformData: transformData.objectTransformInfo,
            });

            return transformData;
          });
          this.unityContext.mainController.SetMutlipleTransforms(_objectList);
        },
        redo: () => {
          const _objectList = objectList.map((unityObject) => {
            const transformData = this._getTransformData(unityObject, 'newTransformData');
            // this.unityContext.mainController.SetTransformation(transformData);

            this._updateObjectPositionAlongAxis({
              objectPositionAlongAxis,
              selectedAxis,
              transformData: transformData.objectTransformInfo,
            });

            return transformData;
          });
          this.unityContext.mainController.SetMutlipleTransforms(_objectList);
        },
      };
    });

    this.selectObject = this.undoRedo.createAction(({ objectList }) => {
      // TODO: compare it to `prevObjectList` instead of prev history node
      // if (this.ignoreSelectObjectCount !== 0) {
      //   this.ignoreSelectObjectCount -= 1;
      //   console.log('sasha this.ignoreSelectObjectCount', this.ignoreSelectObjectCount)
      //   return;
      // }
      const sidebarActiveSection = selectSidebarSection(getAppState());      
      if(isMattressBuilderPage() && sidebarActiveSection !== CURATOR_MENU_SECTION.STYLE){
        return;
      }
      const firstItem = objectList[0]
      if (objectList.length === 1 && firstItem.objectSelectionMode !== OBJECT_SELECTION_MODE.STYLE && firstItem.type === SELECTED_OBJECT_TYPE.LAYOUT && firstItem.isStatic) {
        return;
      }


      if (this._isReplacePropInProgress()) {
        return;
      }

      const lastNode = this.undoRedo.getCurrentNode();
      if (lastNode?.value && lastNode.value.type === 'UNITY_OBJECT___SELECT') {
        if (isEqual(lastNode.value.data.objectList, objectList)) {
          // don't add same object selection
          return;
        }
      }

      const prevObjectList = selectSelectedObjectList(getAppState());

      if(objectList.length === 0 && prevObjectList.length === 0) return;

      return {
        __ACTION__: 'UNITY_OBJECT__SELECT',
        type: 'UNITY_OBJECT___SELECT',
        data: {
          objectList,
          prevObjectList,
        },
        undo: async () => {
          const objectsToSelect = prevObjectList.map(this._mapObjectToSelect);
          this.unityContext.mainController.SelectObjectReceiver(objectsToSelect);
          dispatcher(setSelectedObjectList(prevObjectList));
          await wait(10)
        },
        redo: async () => {
          const objectsToSelect = objectList.map(this._mapObjectToSelect);
          this.unityContext.mainController.SelectObjectReceiver(objectsToSelect);
          dispatcher(setSelectedObjectList(objectList));
          await wait(10)
        },
      };
    }, { pause: true, name: ACTION.SELECT_OBJECT });

    this.delete = this.undoRedo.createAction(async ({ objectList }) => {
      const currentObjectList = selectSelectedObjectList(getAppState());
      // TODO: if previous action was object selection of current object -> remove that action and when we undo -> select an object right away
      // TODO: in other case we don't need to select an object
      // if previous object selection was deleted object -> we need to remove that action and select/unselect objects while undo/redo 
      // if it was different object -> we need to skip object selection in delete action???


      if (!objectList || !objectList.length) return;
      if (this._isReplacePropInProgress()) return;

      
      const currentObjectNames = currentObjectList.map(o => o.objectName)
      const deletedObjectList = objectList.map(o => o.objectName);

      
      // this.undoRedo.stopWatchingAction(ACTION.SELECT_OBJECT);
      // TODO: we need to check if deleted object was part of the selection -> if it wasn't then we don't need to wait for selection change event
      const shouldWait = Boolean(deletedObjectList.find(name => currentObjectNames.includes(name)));

      if (shouldWait) {
        await waitForEventOnce(UnityEvent.GetSelectedObjectInfoOnSelection);
      }
      // await wait(500)
      // this.undoRedo.continueWatchingAction(ACTION.SELECT_OBJECT);

      return {
        __ACTION__: 'UNITY_OBJECT__DELETE',
        undo: async () => {
          await this._enableObjectList(objectList, 'Enable');
        },
        redo: () => {
          this._disableObjectList(objectList, 'Inactive');
        },
      };
    }, { pause: true });

    this.addProp = this.undoRedo.createAction(({ propData, objectList }) => {
      const isReplaceGroupPropActive = selectReplaceGroupPropActive(getAppState());

      if (isReplaceGroupPropActive || this._isReplacePropInProgress()) return;

      return {
        __ACTION__: 'UNITY_OBJECT__ADD_PROP',
        objectList,
        undo: async () => {
          if(objectList?.length > 0) {
            this._disableObjectList(objectList, DELETE_MODE.INACTIVE);
          }
        },
        redo: async () => {
          if(objectList?.length > 0) {
            this._enableObjectList(objectList, CREATE_MODE.ENABLE);
          }
        },
      };
    });

    this.duplicateObject = this.undoRedo.createAction(({ objectList }) => {
      const prevObjectList = [...this.prevObjectSelectionList];
      this.prevObjectSelectionList = [];

      return {
        __ACTION__: 'UNITY_OBJECT__DUPLICATE_PROP',
        objectList,
        undo: () => {
          // this._disableObjectList(objectList, DELETE_MODE.DESTROY);
          this._disableObjectList(objectList, DELETE_MODE.INACTIVE);
          const objectsToSelect = prevObjectList.map(this._mapObjectToSelect);
          this.unityContext.mainController.SelectObjectReceiver(objectsToSelect);
          dispatcher(setSelectedObjectList(prevObjectList));
        },
        redo: async () => {
          // this.unityContext.mainController.SelectObjectReceiver([]);
          // dispatcher(setSelectedObjectList([]));
          const list = await Promise.all(objectList.map(async (objectData) => {
            // const objectDataTransformed = await this._getEnableObjectData(objectData, CREATE_MODE.CREATE, { includeTransform: true });
            const objectDataTransformed = await this._getEnableObjectData(objectData, CREATE_MODE.ENABLE);
            return objectDataTransformed;
            // return objectDataTransformed.propModel;
          }))

          // TODO: add wait for operation end `ReInstantiateDuplicateObjects`
          // await waitForEventOnce(UnityEvent.OnReInstantiateDuplicateObjectCompleted, () => {
          //   this.unityContext.mainController.ReInstantiateDuplicateObjects(list);
          // })
          await Promise.all(list.map(async (objectData) => {
            await this.unityContext.mainController.EnableGameObject(objectData);
          }))
        },
      };
    }, { pause: true });

    this.changeOneAxisPositoin = this.undoRedo.createAction(({ prevValue, nextValue }) => {
      const objectPositionAlongAxis = selectObjectPositionAlongAxis(getAppState());
      let { value, transformType } = objectPositionAlongAxis;

      return {
        __ACTION__: 'UNITY_OBJECT__CHANGE_ONE_AXIS_POSITION',
        prevValue,
        nextValue,
        undo: () => {
          dispatcher(setObjectPositionAlongAxisValue(prevValue));

          if (!Number.isNaN(parseFloat(prevValue))) {
            this.unityContext.shortcutsModule.SetPositionAlongAxis({
              value: parseFloat(prevValue) 
              ? transformType.toUpperCase() === CURSOR_MODE.MOVE ? convertActiveUnitToMeters(parseFloat(prevValue)) : parseFloat(prevValue)
              : 0,
              transformType,
            });
          }
        },
        redo: async () => {
          dispatcher(setObjectPositionAlongAxisValue(nextValue));

          if (!Number.isNaN(parseFloat(nextValue))) {
            this.unityContext.shortcutsModule.SetPositionAlongAxis({
              value: parseFloat(nextValue) 
              ? transformType.toUpperCase() === CURSOR_MODE.MOVE ? convertActiveUnitToMeters(parseFloat(nextValue)) : parseFloat(nextValue)
              : 0,
              transformType,
            });
          }
        },
      };
    });

    // replace prop
    // this.replacePropStart = (newProp, selectedProp) => {
    //   this.replacePropTempData = {
    //     newProp,
    //     selectedProp,
    //   };
    // }

    this.replaceProp = this.undoRedo.createAction(
      async ({ newProp, skipHistory, selectedProp , newReplaceProps }) => {
        this.replacePropInProgress = true;
        // this.replacePropInProgress = false;

        if (skipHistory) return;

        return {
          __ACTION__: 'UNITY_OBJECT__REPLACE_PROP',
          newProp,
          newObjectList:newReplaceProps,
          selectedProp,
          undo: async () => {
            // this._disableObjectList(newObjectList, DELETE_MODE.DESTROY);
            this._disableObjectList(newReplaceProps, DELETE_MODE.INACTIVE);
            await this._enableObjectList(selectedProp, CREATE_MODE.ENABLE);
            dispatcher(setSelectedObjectList(selectedProp));
            const objectsToSelect = selectedProp.map(this._mapObjectToSelect);
            this.unityContext.mainController.SelectObjectReceiver(objectsToSelect);
          },
          redo: async () => {
            // this.unityShortcuts.doReplaceProp(newProp, { skipHistory: true });
            this._disableObjectList(selectedProp, DELETE_MODE.INACTIVE);
            await this._enableObjectList(newReplaceProps, CREATE_MODE.ENABLE);
            dispatcher(setSelectedObjectList(newReplaceProps));
            const objectsToSelect = newReplaceProps.map(this._mapObjectToSelect);
            this.unityContext.mainController.SelectObjectReceiver(objectsToSelect);
          },
        };
      }
    );
  };
}
